import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Info } from "lucide-react";
import { api } from "../../lib/api";

/**
 * CampaignBulkEditDialog
 *
 * Two-mode dialog for editing many campaigns at once:
 *
 *  1. **Shared updates** — one value applied to every selected campaign
 *     (e.g. status → Completed for all). Backed by POST /campaigns/bulk-update.
 *
 *  2. **Per-row updates** — a table where the admin fills in a unique value
 *     for each campaign, e.g. Google Sheet URL. Backed by
 *     POST /campaigns/bulk-update-per-row. Used for fields that are inherently
 *     unique per campaign but tedious to set one-at-a-time.
 *
 * Groups + ad-hoc selection both funnel through the same dialog. The dialog
 * doesn't know or care where the ids came from.
 */

// Fields exposed in "Shared updates" mode. Each entry declares how to render
// the input; the backend enforces which fields it accepts (see `_ALLOWED_BULK_FIELDS`).
const SHARED_FIELDS = [
  { key: "status",           label: "Status",                type: "select",   options: ["Active", "Draft", "Pending", "Completed", "Expired"] },
  { key: "expiry_date",      label: "End date",              type: "date" },
  { key: "billing_status",   label: "Billing status",        type: "select",   options: ["Pending", "Paid", "Overdue"] },
  { key: "campaign_type",    label: "Campaign type",         type: "text" },
  { key: "category_name",    label: "Category",              type: "text" },
  { key: "visible_to_brand", label: "Visible to brand",      type: "boolean" },
  { key: "vr_status",        label: "VR status",             type: "select",   options: [
      { value: "not_generated",     label: "Not generated" },
      { value: "in_progress",       label: "In progress" },
      { value: "pending_review",    label: "Pending review" },
      { value: "approved",          label: "Approved" },
      { value: "shared_with_brand", label: "Shared with brand" },
    ] },
  { key: "group_id",         label: "Assign to group",       type: "group" },
];

// Fields available in "Per-row updates" mode — typically things every campaign
// has a different value for.
const PER_ROW_FIELDS = [
  { key: "google_sheet_id",       label: "Google Sheet ID",   placeholder: "1AbC..." },
  { key: "sheet_tab_creators",    label: "Creators tab",      placeholder: "creators" },
  { key: "sheet_tab_content",     label: "Content tab",       placeholder: "content" },
  { key: "sheet_tab_campaign",    label: "Campaign tab",      placeholder: "campaign" },
  { key: "vr_sheet_url",          label: "VR sheet URL",      placeholder: "https://…" },
  { key: "expiry_date",           label: "End date",          placeholder: "YYYY-MM-DD" },
];


export function CampaignBulkEditDialog({ onClose, campaignIds, campaigns, contextLabel, groups, onSaved }) {
  const [mode, setMode] = useState("shared"); // "shared" | "per_row"
  const [busy, setBusy] = useState(false);

  // Shared-mode state: which fields the admin has ticked + their new values
  const [enabledFields, setEnabledFields] = useState({});
  const [values, setValues] = useState({});

  // Per-row-mode state: which per-row fields are visible in the table + the
  // matrix of values keyed by [campaignId][field].
  // Lazy-init per-row values from campaigns so we don't need a setState-in-effect.
  const [perRowFields, setPerRowFields] = useState({ google_sheet_id: true });
  const [perRowValues, setPerRowValues] = useState(() => {
    const initial = {};
    (campaigns || []).forEach((c) => {
      if (!campaignIds.includes(c.id)) return;
      initial[c.id] = {};
      PER_ROW_FIELDS.forEach((f) => { initial[c.id][f.key] = c[f.key] ?? ""; });
    });
    return initial;
  });

  const selectedCampaigns = useMemo(
    () => (campaigns || []).filter((c) => campaignIds.includes(c.id)),
    [campaigns, campaignIds]
  );

  const applyShared = async () => {
    const patch = {};
    for (const [k, v] of Object.entries(values)) {
      if (enabledFields[k]) patch[k] = v;
    }
    if (Object.keys(patch).length === 0) return toast.error("Tick at least one field to update");
    setBusy(true);
    try {
      const { data } = await api.post("/campaigns/bulk-update", { campaign_ids: campaignIds, patch });
      toast.success(`Updated ${data.modified} of ${data.campaign_count} campaign${data.campaign_count === 1 ? "" : "s"}`);
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk update failed");
    } finally { setBusy(false); }
  };

  const applyPerRow = async () => {
    // Emit one update-row per campaign, containing only the fields the admin
    // enabled AND that have a non-empty value (empty means "leave alone").
    const activeFields = PER_ROW_FIELDS.filter((f) => perRowFields[f.key]);
    if (activeFields.length === 0) return toast.error("Tick at least one field column");
    const updates = [];
    for (const cid of campaignIds) {
      const row = { id: cid };
      let hasAny = false;
      activeFields.forEach((f) => {
        const v = perRowValues[cid]?.[f.key];
        // Explicit empty string clears the field; only skip when the admin
        // has literally not touched the cell (undefined) so untouched rows
        // don't get wiped.
        if (v !== undefined && v !== null) { row[f.key] = v; hasAny = true; }
      });
      if (hasAny) updates.push(row);
    }
    if (updates.length === 0) return toast.error("Nothing to update");
    setBusy(true);
    try {
      const { data } = await api.post("/campaigns/bulk-update-per-row", { updates });
      toast.success(`Updated ${data.applied} campaign${data.applied === 1 ? "" : "s"}${data.skipped ? ` (${data.skipped} skipped)` : ""}`);
      onSaved?.();
      onClose();
    } catch { toast.error("Per-row update failed"); }
    finally { setBusy(false); }
  };

  const renderSharedInput = (f) => {
    const v = values[f.key];
    const set = (nv) => setValues({ ...values, [f.key]: nv });
    if (!enabledFields[f.key]) {
      return <div className="text-xs italic text-[var(--text-secondary)]">Tick the checkbox to edit this field</div>;
    }
    if (f.type === "select") {
      return (
        <select className="wl-input" value={v ?? ""} onChange={(e) => set(e.target.value)} data-testid={`bulk-shared-${f.key}-input`}>
          <option value="">— pick a value —</option>
          {f.options.map((o) => {
            const val = typeof o === "string" ? o : o.value;
            const lbl = typeof o === "string" ? o : o.label;
            return <option key={val} value={val}>{lbl}</option>;
          })}
        </select>
      );
    }
    if (f.type === "date") return <input type="date" className="wl-input" value={v ?? ""} onChange={(e) => set(e.target.value)} data-testid={`bulk-shared-${f.key}-input`} />;
    if (f.type === "boolean") return (
      <select className="wl-input" value={v === undefined ? "" : String(v)} onChange={(e) => set(e.target.value === "true")} data-testid={`bulk-shared-${f.key}-input`}>
        <option value="">— pick —</option>
        <option value="true">Visible</option>
        <option value="false">Hidden</option>
      </select>
    );
    if (f.type === "group") return (
      <select className="wl-input" value={v ?? ""} onChange={(e) => set(e.target.value || null)} data-testid={`bulk-shared-${f.key}-input`}>
        <option value="">— unassign from group —</option>
        {(groups || []).map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
      </select>
    );
    return <input className="wl-input" value={v ?? ""} onChange={(e) => set(e.target.value)} data-testid={`bulk-shared-${f.key}-input`} />;
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !busy && onClose()}>
      <div
        className="wl-card-solid max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="bulk-edit-dialog"
      >
        <div className="p-6 pb-4 border-b border-[var(--border-default)] flex items-start justify-between">
          <div>
            <h3 className="font-display text-lg font-semibold">Bulk edit</h3>
            <p className="text-xs text-[var(--text-secondary)] mt-1">
              Editing <strong>{campaignIds.length}</strong> campaign{campaignIds.length === 1 ? "" : "s"}{contextLabel ? <> in {contextLabel}</> : ""}. Changes apply immediately on save.
            </p>
          </div>
          <button onClick={onClose} className="text-[var(--text-secondary)]" aria-label="Close"><X size={18} /></button>
        </div>

        <div className="flex gap-2 px-6 pt-4 border-b border-[var(--border-default)]">
          <button
            onClick={() => setMode("shared")}
            className={`text-sm px-3 py-2 border-b-2 -mb-px ${mode === "shared" ? "border-[var(--brand)] text-[var(--text-primary)] font-semibold" : "border-transparent text-[var(--text-secondary)]"}`}
            data-testid="bulk-mode-shared"
          >Same value for all</button>
          <button
            onClick={() => setMode("per_row")}
            className={`text-sm px-3 py-2 border-b-2 -mb-px ${mode === "per_row" ? "border-[var(--brand)] text-[var(--text-primary)] font-semibold" : "border-transparent text-[var(--text-secondary)]"}`}
            data-testid="bulk-mode-per-row"
          >Unique value per campaign</button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {mode === "shared" ? (
            <div>
              <div className="text-xs text-[var(--text-secondary)] mb-3 flex items-start gap-2 bg-[var(--bg-surface-warm)] p-3 rounded-lg">
                <Info size={14} className="mt-0.5 shrink-0" />
                <span>Tick the fields you want to change, then pick a value. Unticked fields stay as-is on every selected campaign.</span>
              </div>
              <div className="grid sm:grid-cols-2 gap-4" data-testid="bulk-shared-fields">
                {SHARED_FIELDS.map((f) => (
                  <div key={f.key} className="border border-[var(--border-default)] rounded-lg p-3">
                    <label className="flex items-center gap-2 mb-2">
                      <input
                        type="checkbox"
                        checked={!!enabledFields[f.key]}
                        onChange={(e) => setEnabledFields({ ...enabledFields, [f.key]: e.target.checked })}
                        data-testid={`bulk-shared-${f.key}-toggle`}
                      />
                      <span className="text-sm font-medium">{f.label}</span>
                    </label>
                    {renderSharedInput(f)}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div>
              <div className="text-xs text-[var(--text-secondary)] mb-3 flex items-start gap-2 bg-[var(--bg-surface-warm)] p-3 rounded-lg">
                <Info size={14} className="mt-0.5 shrink-0" />
                <span>Tick the columns you want to edit, then fill in each row. Empty cells leave the current value untouched.</span>
              </div>
              <div className="flex flex-wrap gap-2 mb-4">
                {PER_ROW_FIELDS.map((f) => (
                  <label key={f.key} className="flex items-center gap-1.5 text-xs px-2 py-1 border border-[var(--border-default)] rounded-full">
                    <input
                      type="checkbox"
                      checked={!!perRowFields[f.key]}
                      onChange={(e) => setPerRowFields({ ...perRowFields, [f.key]: e.target.checked })}
                      data-testid={`bulk-per-row-col-${f.key}`}
                    />
                    {f.label}
                  </label>
                ))}
              </div>
              <div className="overflow-x-auto border border-[var(--border-default)] rounded-lg" data-testid="bulk-per-row-table">
                <table className="w-full text-sm">
                  <thead className="bg-[var(--bg-surface-warm)]">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium w-64">Campaign</th>
                      {PER_ROW_FIELDS.filter((f) => perRowFields[f.key]).map((f) => (
                        <th key={f.key} className="text-left px-3 py-2 font-medium">{f.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {selectedCampaigns.map((c) => (
                      <tr key={c.id} className="border-t border-[var(--border-default)]">
                        <td className="px-3 py-2 truncate">
                          <div className="font-medium truncate">{c.campaign_name || "—"}</div>
                          <div className="text-[10px] text-[var(--text-secondary)] font-mono">{c.campaign_id}</div>
                        </td>
                        {PER_ROW_FIELDS.filter((f) => perRowFields[f.key]).map((f) => (
                          <td key={f.key} className="px-3 py-1.5">
                            <input
                              className="wl-input wl-input-sm"
                              value={perRowValues[c.id]?.[f.key] ?? ""}
                              placeholder={f.placeholder}
                              onChange={(e) => setPerRowValues({
                                ...perRowValues,
                                [c.id]: { ...(perRowValues[c.id] || {}), [f.key]: e.target.value },
                              })}
                              data-testid={`bulk-per-row-${c.id}-${f.key}`}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-[var(--border-default)] flex justify-end gap-2 bg-[var(--bg-surface-warm)]">
          <button onClick={onClose} className="wl-btn-secondary" disabled={busy}>Cancel</button>
          {mode === "shared"
            ? <button onClick={applyShared} className="wl-btn-primary" disabled={busy} data-testid="bulk-apply-shared">{busy ? "Applying…" : `Apply to ${campaignIds.length}`}</button>
            : <button onClick={applyPerRow} className="wl-btn-primary" disabled={busy} data-testid="bulk-apply-per-row">{busy ? "Applying…" : "Save changes"}</button>}
        </div>
      </div>
    </div>
  );
}
